export type TextVariable = {
  label: string;
  description: string;
  /** Text to insert (defaults to label if not set) */
  insertValue?: string;
  /** If true, keep the autocomplete open after selection (used for table-level picks) */
  isPartial?: boolean;
};

type TableDef = { name: string; description: string };
type FieldDef = { name: string; description: string };

const TABLES: TableDef[] = [
  { name: "hotspots", description: "Hotspot fields" },
  { name: "panoramas", description: "Panorama fields" },
];

/** Default-visible fields per table (matching CMS "Show all fields" = off). */
const DEFAULT_FIELDS: Record<string, FieldDef[]> = {
  hotspots: [
    { name: "name", description: "Hotspot name" },
    { name: "hotspot_style", description: "Hotspot style" },
  ],
  panoramas: [
    { name: "name", description: "Panorama name" },
  ],
};

// ── Dynamic custom fields (populated at runtime from CMS data) ──

let _customFields: Record<string, FieldDef[]> = {};

/** Register custom CMS fields so they appear in the autocomplete. */
export const setTextVariableCustomFields = (fields: Record<string, FieldDef[]>) => {
  _customFields = fields;
};

/**
 * Two-tier autocomplete for text variables.
 *
 * - Query without a dot → suggest matching table names (partial).
 * - Query with a dot → suggest matching field names for that table (complete).
 */
export const filterTextVariables = (query: string): TextVariable[] => {
  const normalized = query.trim().toLowerCase();
  const dotIndex = normalized.indexOf(".");

  if (dotIndex === -1) {
    // Stage 1: table selection
    return TABLES.filter((t) => !normalized || t.name.startsWith(normalized)).map((t) => ({
      label: t.name,
      description: t.description,
      insertValue: t.name + ".",
      isPartial: true,
    }));
  }

  // Stage 2: field selection – merge default + custom fields, deduplicate by name
  const tableName = normalized.slice(0, dotIndex);
  const fieldQuery = normalized.slice(dotIndex + 1);

  const defaultFields = DEFAULT_FIELDS[tableName] ?? [];
  const customFields = _customFields[tableName] ?? [];
  const seen = new Set(defaultFields.map((f) => f.name));
  const allFields = [...defaultFields];
  for (const f of customFields) {
    if (!seen.has(f.name)) {
      allFields.push(f);
      seen.add(f.name);
    }
  }

  if (allFields.length === 0) return [];

  return allFields
    .filter((f) => !fieldQuery || f.name.startsWith(fieldQuery))
    .map((f) => ({
      label: `[${tableName}.${f.name}]`,
      description: f.description,
    }));
};
